import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DatabaseBackup, Download, Trash2, Play, Loader2, Save, Upload, RotateCcw, HardDrive, Cloud, CheckCircle2, Link2Off, Receipt, RefreshCw, FolderOpen, Folder, ChevronRight, Home } from "lucide-react";
import { toast } from "sonner";
import { DataTable } from "@/components/DataTable";

const fmtSize = (b) =>
  b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`;
const fmtDateTime = (s) =>
  s
    ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" })
    : "-";

export default function Settings() {
  const [settings, setSettings] = useState({ enabled: false, keep_last: 7 });
  const [backups, setBackups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [relinking, setRelinking] = useState(false);
  const [drive, setDrive] = useState({ connected: false });
  const [driveBusy, setDriveBusy] = useState(false);
  const [threshold, setThreshold] = useState("");
  const [consPct, setConsPct] = useState("");
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [folderBrowserOpen, setFolderBrowserOpen] = useState(false);
  const [browserPath, setBrowserPath] = useState([{ id: "root", name: "Il mio Drive" }]);
  const [browserItems, setBrowserItems] = useState([]);
  const [browserLoading, setBrowserLoading] = useState(false);
  const [savingFolder, setSavingFolder] = useState(false);
  const restoreRef = useRef(null);

  const loadBackups = () => api.get("/backups").then((r) => setBackups(r.data));
  const loadDrive = () =>
    api.get("/drive/status").then((r) => setDrive(r.data)).catch(() => {});

  const loadBrowser = async (parentId) => {
    setBrowserLoading(true);
    try {
      const { data } = await api.get("/drive/folders", { params: { parent: parentId } });
      setBrowserItems(data.folders || []);
    } catch (e) {
      toast.error("Impossibile leggere le cartelle di Google Drive");
    } finally {
      setBrowserLoading(false);
    }
  };

  const openFolderBrowser = () => {
    setBrowserPath([{ id: "root", name: "Il mio Drive" }]);
    setFolderBrowserOpen(true);
    loadBrowser("root");
  };

  const enterFolder = (folder) => {
    setBrowserPath((p) => [...p, folder]);
    loadBrowser(folder.id);
  };

  const goToCrumb = (index) => {
    const target = browserPath[index];
    setBrowserPath((p) => p.slice(0, index + 1));
    loadBrowser(target.id);
  };
  useEffect(() => {
    api.get("/backup-settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/settings").then((r) => { setThreshold(String(r.data.receipt_required_above || "")); setConsPct(String(r.data.consumption_alert_pct ?? 15)); }).catch(() => {});
    loadBackups();
    loadDrive();
    const params = new URLSearchParams(window.location.search);
    if (params.get("drive_connected") === "true") {
      toast.success("Google Drive collegato con successo");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("drive_error")) {
      toast.error("Errore durante il collegamento a Google Drive");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connectDrive = async () => {
    setDriveBusy(true);
    try {
      const { data } = await api.get("/drive/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error("Impossibile avviare il collegamento a Google Drive");
      setDriveBusy(false);
    }
  };

  const disconnectDrive = async () => {
    if (!window.confirm("Scollegare Google Drive? I backup non verranno più caricati automaticamente.")) return;
    setDriveBusy(true);
    try {
      await api.post("/drive/disconnect");
      toast.success("Google Drive scollegato");
      loadDrive();
    } catch (e) {
      toast.error("Errore durante lo scollegamento");
    } finally {
      setDriveBusy(false);
    }
  };

  const selectAndSaveFolder = async (folderId, folderName) => {
    setSavingFolder(true);
    try {
      await api.put("/drive/folder", { folder_id: folderId || "", folder_name: folderName || "" });
      toast.success(folderId ? `Cartella impostata: ${folderName}` : "Impostata cartella predefinita");
      setFolderBrowserOpen(false);
      loadDrive();
    } catch (e) {
      toast.error("Errore nel salvataggio della cartella");
    } finally {
      setSavingFolder(false);
    }
  };

  const saveThreshold = async () => {
    setSavingThreshold(true);
    try {
      await api.put("/settings", { receipt_required_above: parseFloat(threshold) || 0, consumption_alert_pct: consPct === "" ? 15 : (parseFloat(consPct) || 0) });
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSavingThreshold(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/backup-settings", {
        enabled: settings.enabled,
        keep_last: parseInt(settings.keep_last, 10) || 7,
      });
      setSettings(data);
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post("/backups/run");
      toast.success("Backup creato");
      loadBackups();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRunning(false);
    }
  };

  const download = async (b) => {
    try {
      const res = await api.get(`/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = b.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore durante il download");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo backup?")) return;
    try {
      await api.delete(`/backups/${id}`);
      toast.success("Backup eliminato");
      loadBackups();
    } catch (e) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const restore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm("Ripristinare il database da questo backup? I dati attuali verranno sostituiti.")) {
      if (restoreRef.current) restoreRef.current.value = "";
      return;
    }
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/backups/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Database ripristinato con successo");
      loadBackups();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRestoring(false);
      if (restoreRef.current) restoreRef.current.value = "";
    }
  };

  const relinkFlux = async () => {
    setRelinking(true);
    try {
      const { data } = await api.post("/integrations/flux/relink-vehicles");
      toast.success(`Collegate ${data.linked} richieste Flux su ${data.total_flux} (mezzi: ${data.vehicles})`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRelinking(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Impostazioni</h1>
        <p className="mt-1 text-sm text-slate-500">Gestisci lo scontrino obbligatorio e il backup del sistema.</p>
      </div>

      <Card className="mb-6 rounded-lg border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <Receipt className="h-5 w-5 text-[#002FA7]" /> Scontrino obbligatorio
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-sm text-slate-500">
            Richiedi l'allegato dello scontrino per le richieste con importo pari o superiore alla soglia. Imposta 0 per non renderlo mai obbligatorio.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label htmlFor="threshold">Soglia importo (€)</Label>
              <Input
                id="threshold"
                type="number"
                min="0"
                step="0.01"
                data-testid="receipt-threshold-input"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                className="mt-1.5 w-40"
                placeholder="es. 50"
              />
            </div>
            <div>
              <Label htmlFor="cons-pct">Allerta consumi — soglia peggioramento (%)</Label>
              <Input
                id="cons-pct"
                type="number"
                min="0"
                max="100"
                step="1"
                data-testid="consumption-threshold-input"
                value={consPct}
                onChange={(e) => setConsPct(e.target.value)}
                className="mt-1.5 w-40"
                placeholder="es. 15"
              />
              <p className="mt-1 text-xs text-slate-400">Badge rosso se il consumo risultante è peggiore del previsto oltre questa percentuale.</p>
            </div>
            <Button onClick={saveThreshold} disabled={savingThreshold} data-testid="save-settings-btn" className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">
              {savingThreshold ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Salva
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6 rounded-lg border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <RefreshCw className="h-5 w-5 text-[#002FA7]" /> Collega mezzi Flux
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-sm text-slate-500">
            Abbina le richieste carburante arrivate da Flux al mezzo PCM corrispondente tramite la <b>targa</b> (o il nome). Necessario perché entrino nei consumi e compaiano nei filtri dello storico. Le nuove richieste Flux vengono collegate automaticamente.
          </p>
          <Button onClick={relinkFlux} disabled={relinking} data-testid="relink-flux-btn" className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">
            {relinking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Collega richieste Flux ora
          </Button>
        </CardContent>
      </Card>

      <Card className="mb-6 rounded-lg border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <DatabaseBackup className="h-5 w-5 text-[#002FA7]" /> Backup automatico
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4">
            <div>
              <p className="font-medium text-slate-900">Backup giornaliero</p>
              <p className="text-sm text-slate-500">Salva ogni giorno l'intero database (JSON + CSV) alle 02:00, con upload su Google Drive se collegato.</p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
              data-testid="backup-enabled-switch"
            />
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label htmlFor="keep">Backup da conservare</Label>
              <Input
                id="keep"
                type="number"
                min="1"
                data-testid="backup-keep-input"
                value={settings.keep_last}
                onChange={(e) => setSettings({ ...settings, keep_last: e.target.value })}
                className="mt-1.5 w-32"
              />
            </div>
            <Button onClick={save} disabled={saving} data-testid="save-backup-settings-btn" className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Salva
            </Button>
            <Button onClick={runNow} disabled={running} variant="outline" data-testid="run-backup-btn" className="rounded-md">
              {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />} Esegui backup ora
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6 rounded-lg border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <HardDrive className="h-5 w-5 text-[#002FA7]" /> Google Drive
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-slate-200 p-4">
            <div>
              {drive.connected ? (
                <div className="flex items-center gap-2" data-testid="drive-connected-status">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <div>
                    <p className="font-medium text-slate-900">Collegato{drive.email ? ` — ${drive.email}` : ""}</p>
                    <p className="text-sm text-slate-500">I backup vengono caricati in: <b>{drive.backup_folder_name || drive.default_folder || "Riforniamo Backups"}</b>.</p>
                  </div>
                </div>
              ) : (
                <div data-testid="drive-disconnected-status">
                  <p className="font-medium text-slate-900">Non collegato</p>
                  <p className="text-sm text-slate-500">Collega un account Google per caricare automaticamente i backup su Drive.</p>
                </div>
              )}
            </div>
            {drive.connected ? (
              <Button onClick={disconnectDrive} disabled={driveBusy} variant="outline" data-testid="drive-disconnect-btn" className="rounded-md border-rose-200 text-rose-600 hover:bg-rose-50">
                {driveBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2Off className="mr-2 h-4 w-4" />} Scollega
              </Button>
            ) : (
              <Button onClick={connectDrive} disabled={driveBusy} data-testid="drive-connect-btn" className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">
                {driveBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Cloud className="mr-2 h-4 w-4" />} Collega Google Drive
              </Button>
            )}
          </div>

          {drive.connected && (
            <div className="mt-4 rounded-lg border border-slate-200 p-4" data-testid="drive-folder-picker">
              <Label className="text-sm font-medium text-slate-700">Cartella di destinazione dei backup</Label>
              <p className="mb-3 mt-1 text-xs text-slate-500">
                Attuale: <b>{drive.backup_folder_name || "Riforniamo Backups (predefinita)"}</b>. Sfoglia il tuo Drive per sceglierne un'altra.
              </p>
              <Button onClick={openFolderBrowser} variant="outline" data-testid="drive-browse-btn" className="rounded-md">
                <FolderOpen className="mr-2 h-4 w-4" /> Sfoglia cartelle Drive
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={folderBrowserOpen} onOpenChange={setFolderBrowserOpen}>
        <DialogContent className="max-w-2xl" data-testid="drive-folder-browser">
          <DialogHeader>
            <DialogTitle className="font-heading">Scegli la cartella di backup</DialogTitle>
            <DialogDescription>Naviga nel tuo Google Drive e seleziona la cartella di destinazione.</DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-center gap-1 text-sm" data-testid="drive-breadcrumb">
            {browserPath.map((c, i) => (
              <span key={c.id} className="flex items-center gap-1">
                {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
                <button
                  onClick={() => goToCrumb(i)}
                  className={`flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-slate-100 ${i === browserPath.length - 1 ? "font-semibold text-slate-900" : "text-[#002FA7]"}`}
                >
                  {i === 0 && <Home className="h-3.5 w-3.5" />}
                  {c.name}
                </button>
              </span>
            ))}
          </div>

          <div className="mt-1 max-h-72 overflow-y-auto rounded-lg border border-slate-200" data-testid="drive-browser-list">
            {browserLoading ? (
              <div className="flex items-center justify-center gap-2 py-12 text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Caricamento...
              </div>
            ) : browserItems.length === 0 ? (
              <p className="py-12 text-center text-sm text-slate-400">Nessuna sottocartella qui.</p>
            ) : (
              browserItems.map((f) => (
                <button
                  key={f.id}
                  onClick={() => enterFolder(f)}
                  data-testid={`browser-folder-${f.id}`}
                  className="flex w-full items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5 text-left last:border-0 hover:bg-slate-50"
                >
                  <span className="flex min-w-0 items-center gap-2 text-slate-800">
                    <Folder className="h-4 w-4 shrink-0 text-amber-500" />
                    <span className="truncate">{f.name}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
                </button>
              ))
            )}
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Button variant="ghost" onClick={() => selectAndSaveFolder("", "")} disabled={savingFolder} data-testid="browser-use-default-btn" className="rounded-md text-slate-500">
              Usa predefinita (Riforniamo Backups)
            </Button>
            <Button
              onClick={() => {
                const c = browserPath[browserPath.length - 1];
                const isRoot = c.id === "root";
                selectAndSaveFolder(isRoot ? "" : c.id, isRoot ? "" : c.name);
              }}
              disabled={savingFolder || browserLoading}
              data-testid="browser-select-current-btn"
              className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]"
            >
              {savingFolder ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Seleziona questa cartella
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="mb-6 rounded-lg border-amber-200 bg-amber-50/40 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <RotateCcw className="h-5 w-5 text-amber-600" /> Ripristino database
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-sm text-slate-600">
            Carica un file di backup .zip per ripristinare il database.{" "}
            <span className="font-medium text-amber-700">Attenzione: i dati attuali verranno sostituiti.</span>
          </p>
          <input ref={restoreRef} type="file" accept=".zip" className="hidden" data-testid="restore-file-input" onChange={restore} />
          <Button onClick={() => restoreRef.current?.click()} disabled={restoring} variant="outline" data-testid="restore-backup-btn" className="rounded-md border-amber-300 text-amber-700 hover:bg-amber-100">
            {restoring ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Carica e ripristina
          </Button>
        </CardContent>
      </Card>

      <Card className="rounded-lg border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Backup disponibili</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="p-4">
            <DataTable
              data={backups}
              testid="backups-table"
              rowTestId={(b) => `backup-row-${b.id}`}
              searchKeys={["filename", "kind"]}
              searchPlaceholder="Cerca backup..."
              initialSort={{ key: "created_at", dir: "desc" }}
              emptyText="Nessun backup disponibile"
              columns={[
                { key: "filename", header: "File", sortable: true, className: "font-medium text-slate-900", cell: (b) => (
                    <div className="flex items-center gap-2">
                      {b.filename}
                      {b.on_drive && (
                        <span title="Su Google Drive" data-testid={`drive-badge-${b.id}`}>
                          <Cloud className="h-4 w-4 text-[#002FA7]" />
                        </span>
                      )}
                    </div>
                  ) },
                { key: "created_at", header: "Data", sortable: true, sortValue: (b) => new Date(b.created_at).getTime(), className: "text-slate-600", cell: (b) => fmtDateTime(b.created_at) },
                { key: "kind", header: "Tipo", sortable: true, className: "capitalize text-slate-600", cell: (b) => b.kind },
                { key: "size", header: "Dimensione", sortable: true, className: "text-slate-600", cell: (b) => fmtSize(b.size) },
                { key: "actions", header: "Azioni", align: "right", cell: (b) => (
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => download(b)} data-testid={`download-backup-${b.id}`} className="rounded-md">
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => del(b.id)} data-testid={`delete-backup-${b.id}`} className="rounded-md border-rose-200 text-rose-600 hover:bg-rose-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) },
              ]}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
